import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, ExternalLink, Mail, KeyRound, Tag, CheckCircle2, Loader2, Save, Truck, MapPin } from "lucide-react";

export default function FournisseurOrderModal({ order, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // group index being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/franchimob/${order.id}/fournisseurs`);
    setData(data);
  }, [order.id]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (g, idx) => {
    setEditing(idx);
    setForm({ fournisseur_id: g.fournisseur_id || "", nom: g.fournisseur_nom === "À assigner" ? "" : g.fournisseur_nom,
      site: g.site || "", email: g.email || "", identifiants_pro: g.identifiants_pro || "", remises: g.remises || "",
      conditions: g.conditions || "", telephone: g.telephone || "" });
  };

  const pickExisting = (id) => {
    const f = data.fournisseurs.find((x) => x.id === id);
    setForm((p) => ({ ...p, fournisseur_id: id, nom: f ? f.nom : p.nom }));
  };

  const saveSupplier = async (g) => {
    setSaving(true);
    try {
      await api.post(`/franchimob/${order.id}/fournisseur`, { ...form, ligne_ids: g.lignes.map((l) => l.id) });
      toast.success("Fournisseur enregistré");
      setEditing(null);
      await load();
    } catch (e) { /* interceptor */ } finally { setSaving(false); }
  };

  const updateLine = async (lid, patch) => {
    try {
      const { data: res } = await api.put(`/franchimob/${order.id}/ligne/${lid}`, patch);
      if (res.email_sent) toast.success("Toutes les lignes commandées → email de suivi envoyé au client");
      else if (res.all_ordered) toast.success("Toutes les lignes sont commandées");
      await load();
      onChanged && onChanged();
    } catch (e) { /* interceptor */ }
  };

  const inp = "w-full border border-stone-300 rounded px-2.5 py-1.5 text-xs";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="fmob-fournisseur-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-stone-200 shrink-0">
          <div>
            <div className="font-display text-xl font-bold">Commandes fournisseurs</div>
            {data && <div className="text-xs text-stone-500 mt-1">
              Cmd {data.order.numero} · {data.order.client}
              {data.order.enseigne && <> · <span className="pill chip-fmob text-[10px]">{data.order.enseigne}</span></>}
            </div>}
            {data?.order.adresse_livraison && <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-1"><MapPin size={11}/> Livraison : {data.order.adresse_livraison}{data.order.contact_livraison ? ` (${data.order.contact_livraison})` : ""}</div>}
          </div>
          <button onClick={onClose} data-testid="fmob-fournisseur-close" className="p-1.5 text-stone-500 hover:text-stone-900"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!data ? <div className="flex items-center justify-center py-12 text-stone-400"><Loader2 className="animate-spin" size={22}/></div>
          : (
            <>
              <div className="flex items-center justify-between text-xs">
                <span className="text-stone-500">{data.progress.ordered}/{data.progress.total} produit(s) commandé(s)</span>
                {data.order.client_tracking_sent && <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13}/> Email de suivi client envoyé</span>}
              </div>

              {data.groups.map((g, idx) => (
                <div key={idx} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`fmob-group-${idx}`}>
                  <div className="bg-stone-50 px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-sm">{g.fournisseur_nom}</div>
                    <button onClick={() => openEdit(g, idx)} data-testid={`fmob-edit-supplier-${idx}`} className="text-xs text-[#C2502F] hover:underline">{g.fournisseur_id ? "Modifier la fiche" : "Assigner un fournisseur"}</button>
                  </div>

                  {editing === idx ? (
                    <div className="p-4 space-y-2 bg-amber-50/40">
                      <div className="grid grid-cols-2 gap-2">
                        <select value={form.fournisseur_id} onChange={(e) => pickExisting(e.target.value)} data-testid="fmob-supplier-select" className={inp}>
                          <option value="">— Nouveau fournisseur —</option>
                          {data.fournisseurs.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                        </select>
                        <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom du fournisseur" data-testid="fmob-supplier-nom" className={inp}/>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="Site internet (URL)" className={inp}/>
                        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email commande" className={inp}/>
                      </div>
                      <input value={form.identifiants_pro} onChange={(e) => setForm({ ...form, identifiants_pro: e.target.value })} placeholder="Identifiants de connexion (login / mot de passe)" className={inp}/>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={form.remises} onChange={(e) => setForm({ ...form, remises: e.target.value })} placeholder="Remise / code promo" className={inp}/>
                        <input value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Accès pro / conditions" className={inp}/>
                      </div>
                      <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Téléphone" className={inp}/>
                      <div className="flex gap-2 justify-end pt-1">
                        <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs border rounded">Annuler</button>
                        <button onClick={() => saveSupplier(g)} disabled={saving} data-testid="fmob-save-supplier" className="px-3 py-1.5 text-xs bg-[#1E3A8A] text-white rounded font-semibold flex items-center gap-1 disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Enregistrer</button>
                      </div>
                    </div>
                  ) : g.fournisseur_id && (
                    <div className="px-4 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600 bg-white border-t border-stone-100">
                      {g.site && <a href={g.site.startsWith("http") ? g.site : `https://${g.site}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-700 hover:underline"><ExternalLink size={12}/> Site</a>}
                      {g.email && <span className="flex items-center gap-1"><Mail size={12} className="text-stone-400"/> {g.email}</span>}
                      {g.identifiants_pro && <span className="flex items-center gap-1"><KeyRound size={12} className="text-stone-400"/> {g.identifiants_pro}</span>}
                      {g.remises && <span className="flex items-center gap-1 text-emerald-700"><Tag size={12}/> {g.remises}</span>}
                    </div>
                  )}

                  <div className="divide-y divide-stone-100">
                    {g.lignes.map((l) => (
                      <div key={l.id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`fmob-line-${l.id}`}>
                        <label className="flex items-center gap-2 flex-1 min-w-[160px] cursor-pointer">
                          <input type="checkbox" checked={!!l.commande} data-testid={`fmob-line-check-${l.id}`} onChange={(e) => updateLine(l.id, { commande: e.target.checked })} className="accent-emerald-600 w-4 h-4"/>
                          <span className={`text-sm ${l.commande ? "line-through text-stone-400" : ""}`}>{l.produit}</span>
                          <span className="text-xs text-stone-400">×{Number(l.qte) || 1}</span>
                        </label>
                        <div className="flex items-center gap-1 text-xs text-stone-500">
                          <Truck size={13}/>
                          <input type="date" value={l.date_livraison_prevue || ""} data-testid={`fmob-line-date-${l.id}`} onChange={(e) => updateLine(l.id, { date_livraison_prevue: e.target.value })} className="border border-stone-200 rounded px-1.5 py-1 text-xs"/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
